import { Box, Button, Chip, CircularProgress, Dialog, IconButton, Typography, useTheme } from '@mui/material'
import React from 'react'
import { useState } from 'react'
import { formatDateTimeForDisplay } from '../../../utils/exam-utilities'
import api from '../../../service/api'
import { Icons, FLAT_COLORS_WITH_BACKGROUND } from '../../../utils/utilities'
import { EditableMathField } from 'react-mathquill'
import parse from 'html-react-parser'
import ConfirmDialog from '../../common/ConfirmDialog'
import { toast } from 'react-toastify'


const AuditExamViewer = ( { handleClose, openState, exam, setExamList } ) => {

    const [questions, setQuestions] = useState( [] )
    const [deleting, setDeleting] = useState( false )
    const [fetchingQuestion, setFetchingQuestion] = useState( false )
    const [deleteDialogState, setDeleteDialogState] = useState( false )
    const [questionFetchError, setQuestionFetchError] = useState( false )

    const { palette, border, layout, shadows } = useTheme()

    const deleteExam = async () => {
        try {
            setDeleting( true )
            await api.deleteExam( exam.exam_id )
            setExamList( prev => prev.filter( e => e.exam_id !== exam.exam_id ) )
            toast( <Typography color="customThemeColor.main"><strong><em>{exam.exam_name}</em></strong> successfully deleted</Typography> )
            handleClose()
        } catch ( e ) {
            toast( e?.response?.data?.message || e?.response?.data || "Something went wrong! try again later" )
        } finally {
            setDeleting( false )
        }
    }

    const fetchExamQuestions = async () => {
        setFetchingQuestion( true )
        try {
            setQuestionFetchError( true )
            const { data: questionsRes } = await api.fetchQuestions( exam.exam_id )
            setQuestions( questionsRes.data || questionsRes )
        } catch ( err ) {
            console.log( err )
            setQuestionFetchError( err?.response?.data?.message || "Opps! An error occured while trying to fetch exam question try again." )
        } finally {
            setFetchingQuestion( false )
        }
    }

    return (
        <Dialog open={openState} slotProps={{
            paper: { sx: { width: "99%", maxWidth: "1200px", display: "flex", flexDirection: "column" } }
        }}>
            <ConfirmDialog enableSecondaryConfirmation secondaryConfirmationText='removeParticipationsAndDelete' waiting={deleting} status={deleteDialogState} waitingText="Deleting..." actionName="Yes! delete it" confirmAction={deleteExam} cancelAction={() => setDeleteDialogState( false )} message={<p>All the participation details of the exam <em> <strong>{exam.exam_name}</strong> </em> will be lost!</p>} />
            <Box padding={layout.pagePadding} borderBottom={border[1]} boxShadow={shadows[1]} bgcolor={palette.background.paper} position="sticky" top="0" display="flex" alignItems="flex-start" justifyContent="space-between" sx={{ zIndex: 10 }}>
                <Box>
                    <Typography variant="h1" gutterBottom fontWeight="600" component="h2" fontSize="16px">{exam.exam_name}</Typography>
                    <Typography variant='body2'>{`${formatDateTimeForDisplay( exam.exam_start_date, exam.exam_start_time )} - ${formatDateTimeForDisplay( exam.exam_end_date, exam.exam_end_time )}`}</Typography>
                    {exam.exam_duration !== "null" && exam.exam_duration !== null && <Typography color='textSecondary' fontSize="12px" variant='body2'> {`Duration: ${exam.exam_duration.split( ":" )[0]} Hours ${exam.exam_duration.split( ":" )[1]} Minutes`}</Typography>}
                    <Typography color='textSecondary' fontSize="12px" variant='body2'>Maximum Attempts: {exam.maximum_attempts}</Typography>
                </Box>
                <IconButton onClick={handleClose}>{Icons.default.CloseIcon}</IconButton>
            </Box>
            <Box padding={layout.pagePadding} display="flex" flexDirection="column" gap={layout.sectionsGap}>
                <Box border={border[1]} borderRadius={layout.borderRadius} padding="10px" display="flex" flexDirection="column" >
                    <Typography color='error' fontWeight="600">Danger Zone</Typography>
                    <Typography variant='subtitle2' fontSize="12px" gutterBottom display="block" color='textSecondary'>Delete this exam and all of its student attempts.</Typography>
                    <Button onClick={() => setDeleteDialogState( true )} sx={{ textTransform: "capitalize", width: "fit-content", marginTop: "10px" }} startIcon={Icons.default.DeleteIcon} color="errorMessage" variant='contained' disableElevation>Delete</Button>
                </Box>

                <Typography variant='body1'>{exam.exam_description}</Typography>
                <Box>
                    <Typography variant='subtitle2' color="textSecondary">Topics</Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", }} flexWrap="wrap" gap="5px">
                        {exam.exam_topics && exam.exam_topics.map( ( topic, index ) => (
                            <Chip key={topic} label={topic} sx={{ marginRight: "5px", borderRadius: "5px", border: border[1], bgcolor: palette.contentBg, fontWeight: "600" }} />
                        ) )}
                    </Box>
                </Box>
                <Box>
                    <Typography variant='subtitle2' color="textSecondary">Exam for batch(es):</Typography>
                    <Box display="flex" gap="10px" flexWrap="wrap" alignItems="center">
                        {exam.batches_ids && exam.batches_ids.map( ( batch, index ) => (
                            <Chip key={batch} label={batch} sx={{ marginRight: "5px", borderRadius: "5px", border: border[1], bgcolor: palette.contentBg, fontWeight: "600" }} />
                        ) )}
                        {( !exam.batches_ids || exam.batches_ids.length === 0 ) && <Typography variant='body2' color="textSecondary">Exam is not yet assigned to batches!</Typography>}
                    </Box>
                </Box>
                <Box >
                    {questions.length === 0 && <Button disabled={fetchingQuestion} startIcon={fetchingQuestion ? <CircularProgress size={14} /> : Icons.default.ViewListIcon} variant="contained" onClick={fetchExamQuestions} disableElevation color="secondary" sx={{ textTransform: "capitalize" }}>View Questions</Button>}
                    {questions.length > 0 && <Box display="flex" marginTop="10px" flexDirection="column" gap="10px">
                        {questions.map( ( question, index ) => (
                            <Box border={border[1]} padding="10px" borderRadius={layout.borderRadius} bgcolor={palette.contentBg} sx={{ fontSize: "14px", borderRadius: "5px", "& p": { margin: "0 " } }} key={question.question.question_id}>
                                <Typography variant='subtitle2' fontSize="10px" > <em>Question {index + 1}</em> </Typography>
                                <Box>
                                    <Box color="textSecondary">
                                        {question.question.question_text.trim().includes( 'LATEX-' ) ? <EditableMathField latex={question.question.question_text.trim().split( "LATEX-" )[1]} id='static-latex' style={{ padding: "0px", fontFamily: "'Roboto' sans-serif !important", fontWeight: "bold", color: palette.shadedText }}> </EditableMathField> : parse( question.question.question_text.trim() )}
                                    </Box>
                                </Box>
                            </Box>
                        ) )}
                    </Box>}
                    {questionFetchError && <Typography color="errorMessage.main" variant='subtitle2'>{questionFetchError}</Typography>}
                </Box>
            </Box>
        </Dialog>
    )
}

export default AuditExamViewer
